import { Component, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  skip,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';

import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

import { IRfq } from '../../../rfq/interfaces';
import { SnackBarConfig } from '../../../shared/configs';
import {
  ChannelProductMappingApiService,
  RfqApiService,
} from '../../../shared/services';
import { trackById } from '../../../shared/util';
import { EProductMappingType } from '../../interfaces';

import { AppIntegrationAccountProductsApiService } from './../../../shared/services/api/app-integration/app-integration-account-products-api.service';

interface ICellRendererParamsChannel extends ICellEditorParams {
  mappingType :EProductMappingType;
}

@Component({
  selector: 'lum-channel-product-mapping-search-sku-cell-editor',
  templateUrl: './channel-product-mapping-search-sku-cell-editor.component.html',
  styleUrls: ['./channel-product-mapping-search-sku-cell-editor.component.scss'],
})
export class ChannelProductMappingSearchSkuCellEditorComponent
  implements ICellEditorAngularComp, OnDestroy
{
  searchControl = new UntypedFormControl();
  searchTerm :string = '';
  selectedItem :Partial<IRfq> | null = null;
  previousSelectedItem :Partial<IRfq> | null = null;
  relations = ['defaultSellingUnit'];

  trackById = trackById;

  private readonly _rfqs = new BehaviorSubject<IRfq[]>(<IRfq[]>[]);
  rfqs$ = this._rfqs.asObservable();

  private readonly _loadingRfqs = new BehaviorSubject<boolean>(<boolean>false);
  loadingRfqs$ = this._loadingRfqs.asObservable();

  private params :ICellRendererParamsChannel;
  private onDestroy$ = new Subject();

  constructor(
    private rfqApiService :RfqApiService,
    private channelProductMappingApiService :ChannelProductMappingApiService,
    private appIntegrationAccountProductsApiService :AppIntegrationAccountProductsApiService,
    private snackBar :MatSnackBar,
    private dialog :MatLegacyDialog,
  ) {}

  agInit(params :ICellRendererParamsChannel) :void {
    this.params = params;
    const { data } = this.params;

    switch (this.params.mappingType) {
      case EProductMappingType.CHANNEL:
        this.selectedItem = data?.productOverride?.lumSku || data?.lumSku || null;
        break;

      default:
        this.selectedItem = data?.lumSku;
        break;
    }

    this.previousSelectedItem = this.selectedItem;
    this.initSearchControlListener();
  }

  getValue() :Partial<IRfq> | null {
    return this.selectedItem;
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  selectValue(item :IRfq) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Update',
          message: `Are you sure you want to proceed with updating the mapping?`,
          confirmButton: 'Yes',
          cancelButton: 'No',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result :boolean) => {
        if (result) {
          this.selectedItem = item;
          this.updateSkuMapping();
        }
      });
  }

  displaySelectedItem() :string {
    return this.formattedSelectedItem;
  }

  removeSelectedItem() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: 'Are you sure you want to delete this mapping?',
          confirmButton: 'Yes',
          cancelButton: 'No',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result :boolean) => {
        if (result) {
          this.selectedItem = null;
          this.updateSkuMapping();
        }
      });
  }

  private updateSkuMapping() :void {
    this.updateChannelPushStockOmsProductSkuMapping();
  }

  private updateChannelPushStockOmsProductSkuMapping() :void {
    const payload = {
      channelId: this.params.data?.channelId,
      lumSkuId: (this.selectedItem?.id || 0).toString(), // 0 = unmap sku
    };

    switch (this.params.mappingType) {
      case EProductMappingType.CHANNEL:
        this.channelProductMappingApiService
          .saveChannelProductOverride(this.params.data.id, payload)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.params.stopEditing();
              this.params.api.stopEditing();
            }),
          )
          .subscribe({
            next: (response :any) => {
              this.snackBar.open('Successfully mapped sku.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });

              this.params.node.setData({
                ...this.params.data,
                productOverride: response,
              });

              this.params.api.refreshCells({
                rowNodes: [this.params.node],
                force: true,
              });
            },
            error: (error :any) => {
              // rollback
              this.selectedItem = this.previousSelectedItem;

              this.snackBar.open('Failed to map sku.', 'Error', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            },
          });
        break;

      case EProductMappingType.APP_INTEGRATION_ACCOUNT:
        delete payload.channelId;
        this.appIntegrationAccountProductsApiService
          .update(this.params.data.id, {
            lumSkuId: this.selectedItem?.id || 0,
          })
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.params.stopEditing();
              this.params.api.stopEditing();
            }),
          )
          .subscribe({
            next: (response :any) => {
              this.snackBar.open('Successfully mapped sku.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              // Refresh the grid
              this.params.api.refreshCells();
            },
            error: (error :any) => {
              // rollback
              this.selectedItem = this.previousSelectedItem;

              this.snackBar.open('Failed to map sku.', 'Error', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            },
          });

        break;
    }
  }

  private initSearchControlListener() :void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        startWith([]),
        skip(1),
        takeUntil(this.onDestroy$),
      )
      .subscribe((keyword :string) => {
        this.searchRfqs(keyword);
      });
  }

  private searchRfqs(keyword :string) :void {
    this._loadingRfqs.next(true);
    this.rfqApiService
      .searchAsyncV2({ sku: keyword, relations: this.relations })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loadingRfqs.next(false);
        }),
      )
      .subscribe({
        next: (rfqs :IRfq[]) => {
          this._rfqs.next(rfqs);
        },
        error: (error :any) => {
          this.snackBar.open('Error when seaching sku.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get formattedSelectedItem() :string {
    if (!this.selectedItem) {
      return '';
    }

    return `[${this.selectedItem.internalSku || 'N/A'}] ${
      this.selectedItem.productName
    }`;
  }
}
